/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 */

import {Component, Input} from '@angular/core';
import {CommonModule} from '@angular/common';
import {IxModule} from '@siemens/ix-angular';
import {TranslateModule} from '@ngx-translate/core';
import {BaseFormFieldDirective} from '../../base-form-field.directive';
import {FieldLabelComponent} from '../field-label/field-label.component';
import {FormFieldAttribute} from '../../../../../../../models/FormCategory';
import {
  flagAssetPath,
  PLATFORM_LANGUAGE_CODES,
} from '../../../../../../shared/pass-viewer/utils/flag-asset.util';
import {findLanguageColumn} from '../../../../../../shared/pass-viewer/utils/language-column.util';
import {languageDisplayName} from '../../../../../../shared/pass-viewer/utils/language-display-name.util';

/**
 * Editor language select for one row of a `list:language` field (a row is
 * `{languageCode | language, ...value fields}`). Offers the languages the
 * schema allows (the language column's `options`, otherwise the platform
 * language set) minus the languages already used by sibling rows, shows the
 * selected language's flag next to the select, and flags rows whose language
 * is duplicated (only reachable through imported or directly edited data).
 *
 * Writes only to the passport's own form data through the shared save path;
 * it never touches the Viewer-only `ContentLanguageSelectionService`.
 */
@Component({
  selector: 'app-language-select-input',
  imports: [CommonModule, IxModule, TranslateModule, FieldLabelComponent],
  templateUrl: './language-select-input.component.html',
  styleUrl: '../input-components.scss',
})
export class LanguageSelectInputComponent extends BaseFormFieldDirective {
  @Input({required: true}) row!: number;

  /** The platform's fixed language codes, offered when the schema declares no options. */
  readonly platformLanguageCodes = PLATFORM_LANGUAGE_CODES;

  /** The row's language sub-attribute (`languageCode` or `language`, wherever it is declared). */
  get languageAttribute(): FormFieldAttribute | undefined {
    return findLanguageColumn(this.attribute.attributes);
  }

  /** Translated label of the language sub-attribute, used in the row's accessible name. */
  get label(): string {
    return this.languageAttribute?.translation?.label
      ? this.translate.instant(this.languageAttribute.translation.label)
      : '';
  }

  /** Language code stored in this row, or an empty string when none is set. */
  get selectedLanguageCode(): string {
    const languageValuePath = this.languageValuePath();
    if (!languageValuePath) {
      return '';
    }
    const languageCode = this.currentRow()?.[languageValuePath];
    return typeof languageCode === 'string' ? languageCode : '';
  }

  /** Template binding for {@link availableLanguageCodesFor}. */
  get availableLanguageCodes(): string[] {
    return this.availableLanguageCodesFor();
  }

  /** Template binding for {@link selectedFlagPath}. */
  get flagPath(): string | null {
    return this.selectedFlagPath();
  }

  /** Template binding for {@link isDuplicateLanguage}. */
  get hasDuplicate(): boolean {
    return this.isDuplicateLanguage();
  }

  /**
   * Writes the picked language code into this row and saves the field
   * through the shared form save path.
   *
   * @param event - The `ix-select` value change (event, single value or array).
   */
  async onLanguageRowChange(
    event: CustomEvent<string | string[]> | string | string[],
  ): Promise<void> {
    const detail =
      typeof event === 'string' || Array.isArray(event) ? event : event.detail;
    const languageCode = Array.isArray(detail) ? (detail[0] ?? '') : detail;
    const languageValuePath = this.languageValuePath();
    const currentRow = this.currentRow();
    if (!languageValuePath || !currentRow) {
      return;
    }
    currentRow[languageValuePath] = languageCode;
    await this.onControlValueChanged(this.passData, this.saveFunction);
  }

  /**
   * Language codes this row may choose: the schema's allowed codes (or the
   * platform set) except those used by sibling rows. The row's own current
   * code is always kept, even when it is not in the allowed list (e.g.
   * imported data), so the select can display it.
   *
   * @returns The selectable language codes in schema/platform order.
   */
  availableLanguageCodesFor(): string[] {
    const allowedCodes = this.allowedLanguageCodes();
    const languageValuePath = this.languageValuePath();
    if (!languageValuePath) {
      return allowedCodes;
    }
    const ownCode = this.selectedLanguageCode;
    const codesUsedBySiblings = new Set(
      this.languageRows()
        .filter((_, rowIndex) => rowIndex !== this.row)
        .map((row) => row[languageValuePath]),
    );
    const selectableCodes = allowedCodes.filter(
      (code) => code === ownCode || !codesUsedBySiblings.has(code),
    );
    if (ownCode && !selectableCodes.includes(ownCode)) {
      selectableCodes.unshift(ownCode);
    }
    return selectableCodes;
  }

  /**
   * Whether this row's language is also used by another row of the same field.
   *
   * @returns True when the row's language code occurs more than once.
   */
  isDuplicateLanguage(): boolean {
    const languageValuePath = this.languageValuePath();
    const ownCode = this.selectedLanguageCode;
    if (!languageValuePath || !ownCode) {
      return false;
    }
    const occurrences = this.languageRows().filter(
      (row) => row[languageValuePath] === ownCode,
    ).length;
    return occurrences > 1;
  }

  /**
   * Local flag asset path for this row's language.
   *
   * @returns The flag path, or null when no language is set or it is outside the allowlist.
   */
  selectedFlagPath(): string | null {
    return flagAssetPath(this.selectedLanguageCode);
  }

  /**
   * Returns the language's name in the current UI language via `Intl.DisplayNames`.
   *
   * @param languageCode - Language code to name (for example: en, de).
   * @returns The display name, or the uppercase code if no name is available.
   */
  displayNameFor(languageCode: string): string {
    return languageDisplayName(
      languageCode,
      this.translate.currentLang || this.translate.defaultLang,
    );
  }

  /**
   * Codes the schema allows for the language column: its declared `options`
   * when present (e.g. `de`, `en`, `fr` for `machinery-1.2.0`), otherwise the
   * platform language set.
   */
  private allowedLanguageCodes(): string[] {
    const optionCodes = (this.languageAttribute?.options ?? [])
      .map((option) => option.value)
      .filter((value): value is string => typeof value === 'string' && !!value);
    return optionCodes.length > 0
      ? optionCodes
      : Array.from(this.platformLanguageCodes);
  }

  private languageRows(): Array<Record<string, unknown>> {
    return Array.isArray(this.passData)
      ? (this.passData as Array<Record<string, unknown>>)
      : [];
  }

  private currentRow(): Record<string, unknown> | undefined {
    return this.languageRows()[this.row];
  }

  private languageValuePath(): string | undefined {
    return this.languageAttribute?.valuePath;
  }
}
